import { inject, Injectable, signal } from '@angular/core';
import { DictionaryService } from './dictionary.service';
import { SnackbarConfig, SnackbarPosition, SnackbarState, SnackbarType, SnackbarSize } from '../components/snackbar/snackbar.interface';
import { getMessageByCode, getTypeByCode } from '../constants/message-codes';

@Injectable({
    providedIn: 'root',
})
export class SnackbarService {
    private dictionaryService = inject(DictionaryService);

    private readonly DEFAULT_DURATION = 5000; // 5 seconds
    private readonly DEFAULT_SIZE: SnackbarSize = 'small';
    private timeoutId: ReturnType<typeof setTimeout> | null = null;

    // Reactive state for the snackbar
    public state = signal<SnackbarState>({
        isVisible: false,
        message: '',
        type: 'default',
        size: 'small',
        position: 'bottom-right',
    });

    /**
     * Gets the default position based on language direction
     * English/LTR: bottom-right
     * Arabic/RTL: bottom-left
     */
    private getDefaultPosition(): SnackbarPosition {
        return this.dictionaryService.language === 'ar' ? 'bottom-left' : 'bottom-right';
    }

    /**
     * Shows a snackbar with the given configuration
     * @param config - Snackbar configuration
     */
    show(config: SnackbarConfig): void {
        // Clear any existing timeout
        this.clearTimeout();

        // Update the state
        this.state.set({
            isVisible: true,
            message: config.message,
            type: config.type,
            size: config.size ?? this.DEFAULT_SIZE,
            position: config.position ?? this.getDefaultPosition(),
        });

        // Set auto-dismiss timeout if duration is not 0
        const duration = config.duration ?? this.DEFAULT_DURATION;
        if (duration > 0) {
            this.timeoutId = setTimeout(() => {
                this.hide();
            }, duration);
        }
    }

    /**
     * Hides the snackbar
     */
    hide(): void {
        this.clearTimeout();
        this.state.update((current) => ({
            ...current,
            isVisible: false,
        }));
    }

    /**
     * Shows a default/neutral snackbar
     * @param message - Message to display (can be a dictionary key)
     * @param options - Optional size, position, and duration
     */
    showDefault(
        message: string,
        options?: { duration?: number; size?: SnackbarSize; position?: SnackbarPosition }
    ): void {
        this.show({ message: this.translate(message), type: 'default', ...options });
    }

    /**
     * Shows an info snackbar (Jellyfish theme)
     * @param message - Message to display (can be a dictionary key)
     * @param options - Optional size, position, and duration
     */
    showInfo(
        message: string,
        options?: { duration?: number; size?: SnackbarSize; position?: SnackbarPosition }
    ): void {
        this.show({ message: this.translate(message), type: 'info', ...options });
    }

    /**
     * Shows a success snackbar
     * @param message - Message to display (can be a dictionary key)
     * @param options - Optional size, position, and duration
     */
    showSuccess(
        message: string,
        options?: { duration?: number; size?: SnackbarSize; position?: SnackbarPosition }
    ): void {
        this.show({ message: this.translate(message), type: 'success', ...options });
    }

    /**
     * Shows a warning snackbar
     * @param message - Message to display (can be a dictionary key)
     * @param options - Optional size, position, and duration
     */
    showWarning(
        message: string,
        options?: { duration?: number; size?: SnackbarSize; position?: SnackbarPosition }
    ): void {
        this.show({ message: this.translate(message), type: 'warning', ...options });
    }

    /**
     * Shows an error snackbar
     * @param message - Message to display (can be a dictionary key)
     * @param options - Optional size, position, and duration
     */
    showError(
        message: string,
        options?: { duration?: number; size?: SnackbarSize; position?: SnackbarPosition }
    ): void {
        this.show({ message: this.translate(message), type: 'error', ...options });
    }

    /**
     * Shows a snackbar based on an API message code.
     * Auto-detects success/error type from the code prefix ('S' / 'E').
     * Falls back to showDefault() if the code is not found in the map.
     * @param code - The API messageCode (e.g. 'S0701001')
     * @param options - Optional size, position, and duration
     */
    showByCode(
        code: string | null | undefined,
        options?: { duration?: number; size?: SnackbarSize; position?: SnackbarPosition }
    ): void {
        if (!code) {
            this.showDefault('Unknown error', options);
            return;
        }
        const message = getMessageByCode(code, this.dictionaryService.language);
        if (!message) {
            this.showDefault(code, options);
            return;
        }
        const type = getTypeByCode(code);
        this.show({ message, type, ...options });
    }

    /**
     * Translates a message if it's a dictionary key, otherwise returns the message as-is
     * @param message - Message or dictionary key
     */
    private translate(message: string): string {
        const translation = this.dictionaryService.get(message);
        return translation ?? message;
    }

    /**
     * Clears the auto-dismiss timeout
     */
    private clearTimeout(): void {
        if (this.timeoutId) {
            clearTimeout(this.timeoutId);
            this.timeoutId = null;
        }
    }
}